
import { useState, useEffect } from "react";
import { TransactionForm } from "@/components/TransactionForm";
import { FilterSection } from "@/components/FilterSection";
import { TransactionTable } from "@/components/TransactionTable";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Index = () => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { toast: uiToast } = useToast();

  // Check if app is already installed
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
  }, []);

  // Get current authenticated user
  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setCurrentUser(data.user);
        console.log("Current user:", data.user.email);
      }
    };
    
    getUser();
    
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setCurrentUser(session?.user || null);
    });
    
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Capture install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show install prompt
    deferredPrompt.prompt();

    // Wait for user choice
    const choiceResult = await deferredPrompt.userChoice;

    if (choiceResult.outcome === 'accepted') {
      uiToast({
        title: "Instalação iniciada",
        description: "O aplicativo está sendo instalado no seu dispositivo",
      });
      setIsInstalled(true);
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
  };

  // Function to manually trigger a refresh of all transaction-related data
  const refreshAllData = async () => {
    // Prevent rapid consecutive refreshes
    if (Date.now() - lastRefresh < 2000) {
      toast.info("Por favor, aguarde antes de atualizar novamente");
      return;
    }
    
    // Set locking flags
    setIsRefreshing(true);
    setLastRefresh(Date.now());
    
    try {
      // Show visual feedback immediately
      document.body.style.cursor = 'wait';
      
      console.log("Refreshing all data...");
      
      // Invalidate all transaction-related queries with force refresh
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["transactions"], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ["financial-summary"], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ["cash-flow"], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ["transactions-detail"], refetchType: 'all' })
      ]);
      
      // Forçar atualização da tabela de transações usando evento personalizado
      window.dispatchEvent(new CustomEvent('forceTransactionRefresh'));
      
      // Also update our local trigger to force component refreshes
      setRefreshTrigger(prev => prev + 1);
      
      toast.success("Dados atualizados com sucesso");
    } catch (error) {
      console.error("Erro ao atualizar dados:", error);
      toast.error("Erro ao atualizar dados");
    } finally {
      setIsRefreshing(false);
      document.body.style.cursor = 'default';
    }
  };

  // Listen for transaction events that should trigger a refresh
  useEffect(() => {
    const handleTransactionChange = () => {
      console.log("Transaction change detected, refreshing data");
      
      // Only invalidate queries without showing loading states
      queryClient.invalidateQueries({ 
        queryKey: ["transactions"],
        refetchType: 'all'
      });
      
      queryClient.invalidateQueries({ 
        queryKey: ["financial-summary"],
        refetchType: 'all'
      });
    };
    
    window.addEventListener("transaction-changed", handleTransactionChange);
    
    // Add listener for the more specific transaction-created event
    const handleTransactionCreated = (e: CustomEvent) => {
      const { transaction } = e.detail;
      console.log("Transaction created:", transaction?.id);
      
      // Show a confirmation with the transaction details
      toast.success(`Transação registrada com sucesso${currentUser ? ' por ' + currentUser.email : ''}!`);
      
      // Trigger refresh without UI loading state
      queryClient.invalidateQueries({
        queryKey: ["transactions"],
        refetchType: 'all'
      });
    };
    
    window.addEventListener("transaction-created", handleTransactionCreated as EventListener);
    
    return () => {
      window.removeEventListener("transaction-changed", handleTransactionChange);
      window.removeEventListener("transaction-created", handleTransactionCreated as EventListener);
    };
  }, [queryClient, currentUser]);

  return (
    <div className="w-full">
      <div className="max-w-full px-2 sm:px-4 md:px-6 mx-auto space-y-2 sm:space-y-4 md:space-y-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Fluxo de Caixa</h1>
          
          <div className="flex items-center gap-2">
            {!isInstalled && deferredPrompt && (
              <Button
                variant="outline"
                className="flex items-center gap-2 border-[#0073B7] text-[#0073B7] hover:bg-[#0073B7]/10"
                onClick={handleInstallClick}
              >
                <Download className="h-4 w-4" />
                Baixar Aplicativo
              </Button>
            )}
            
            {isRefreshing ? (
              <div className="flex items-center text-sm text-[#0073B7]">
                <div className="animate-spin h-4 w-4 border-2 border-[#0073B7] border-t-transparent rounded-full mr-2"></div>
                Atualizando...
              </div>
            ) : (
              <button 
                onClick={refreshAllData}
                className="text-sm text-[#0073B7] hover:text-[#004080] transition-colors"
              >
                Atualizar dados
              </button>
            )}
          </div>
        </div>
        
        <div className="space-y-2 sm:space-y-4 md:space-y-6">
          <div className="p-2 sm:p-4 md:p-6 rounded-lg bg-card">
            <TransactionForm key={`transaction-form-${refreshTrigger}`} />
          </div>

          <div className="p-2 sm:p-4 md:p-6 rounded-lg bg-card">
            <FilterSection />
          </div>

          <div className="overflow-hidden rounded-lg bg-card">
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <TransactionTable key={`transaction-table-${refreshTrigger}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
